import range from "lodash/range";
import { useEffect, useState } from "react";

type PaginationMode = "scroll" | "slice";
interface PaginationProps<T> {
  key?: string;
  items: T[];
  itemsPerPage: number;
  mode?: PaginationMode;
}

export default function usePagination<T = any>(props: PaginationProps<T>) {
  const [currentPage, setCurrentPage] = useState<number>(1);

  const maxPages = Math.ceil(props.items.length / props.itemsPerPage);
  const pages = range(1, maxPages + 1);

  const hasNextPage = pages.length > 1 && pages.includes(currentPage + 1);
  const hasPreviousPage = pages.length > 1 && currentPage > 1 && pages.includes(currentPage - 1);

  useEffect(() => {
    if (props.key !== undefined) {
      setCurrentPage(1);
    }
  }, [props.key]);

  const currentItems = getCurrentItems(props.mode || "slice", props.items, currentPage, props.itemsPerPage);

  return {
    items: props.items,
    itemsPerPage: props.itemsPerPage,
    pages,
    currentItems,
    currentPage,
    setCurrentPage,
    reset: () => setCurrentPage(1),
    hasNextPage,
    hasPreviousPage,
    goToNextPage: () => setCurrentPage(hasNextPage ? currentPage + 1 : currentPage),
    goToPreviousPage: () => setCurrentPage(hasPreviousPage ? currentPage - 1 : currentPage),
  };
}

function getCurrentItems<Item>(mode: PaginationMode, items: Item[], currentPage: number, itemsPerPage: number): Item[] {
  const _currentPage = currentPage > 0 ? currentPage : 1;
  if (mode === "slice") {
    return [...items].splice((_currentPage - 1) * itemsPerPage, itemsPerPage);
  }

  const currentItems = [...items].splice((_currentPage - 1) * itemsPerPage, itemsPerPage);
  if (currentItems.length === itemsPerPage) {
    return currentItems;
  }

  const delta = itemsPerPage - currentItems.length;
  return [...items].splice((_currentPage - 1) * itemsPerPage - delta, itemsPerPage);
}
